import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { useCurrentOrganizationId } from './currentOrganization';
import { queryGet } from './fetch';
import { OfferColors, OfferType } from './offers';
import { Product } from './plan';

export enum Feature {
  ACCOUNT_MANAGER = 'accountManager',
  VERIFY_SETUP = 'verifySetup',
  TURNKEY = 'turnkey',
  AUTOMATIC_INVITATION_LAUNCH = 'automaticInvitationLaunch',

  NDA = 'nda',

  CONTINUOUS_STUDIES = 'continuousStudies',

  EARLY_PHONE_DISPLAY = 'earlyPhoneDisplay',

  CALENDAR_INTEGRATION = 'calendarIntegration',
  CONFERENCING_INTEGRATION_8X8 = 'conferencingIntegration8x8',
  CONFERENCING_INTEGRATION_ZOOM = 'conferencingIntegrationZoom',
  CONFERENCING_INTEGRATION_INTEGRATED = 'conferencingIntegrationIntegrated',

  SCREENING_FORM = 'screeningForm',
  SCREENING_FORM_LOGIC = 'screeningFormLogic',
  SCREENING_DUPLICATION = 'screeningDuplication',

  CUSTOM_PANEL = 'customPanel',
  CSV_IMPORT = 'csvImport',
  CUSTOM_PARTICIPANT_FIELD = 'customParticipantField',
  PANEL_SUBSCRIPTION_FORM = 'panelSubscriptionForm',
  MEETING_ATTENDEES = 'meetingAttendees',

  SEND_INVOICE = 'sendInvoice',
  ASK_DOCUMENT = 'askDocument',
  QUOTA = 'quota',
  AI_SUGGESTION = 'aiSuggestion',
}

export type Capabilities = Record<Feature, boolean>;

export interface ShortOfferDto {
  id: string;
  name?: string;
  shortName?: string;
  color?: OfferColors;
  type: OfferType;
  referencePrice?: number;
}

export interface ProductOffer {
  product: Product;
  offers: ShortOfferDto[];
}

export type CapabilitiesProductOffer = Record<Feature, ProductOffer>;

export const usePlanCapabilities = (id?: string) => {
  return useQuery<Capabilities>(
    ['plans', id, 'capabilities'],
    queryGet(`/v2/plans/${id}/capabilities`),
    {
      enabled: !!id,
    },
  );
};

export const useOrganizationCapabilities = (id?: string) => {
  return useQuery<Capabilities>(
    ['organizations', id, 'capabilities'],
    queryGet(`/v2/organizations/${id}/capabilities`),
    {
      enabled: !!id,
    },
  );
};

export const useCapabilitiesOffers = () => {
  return useQuery<CapabilitiesProductOffer>(
    ['capabilities-offers'],
    queryGet(`/v2/capabilities-offers`),
    {
      staleTime: 10 * 60 * 1000,
    },
  );
};

export const useCapabilities = (planId?: string, organisationId?: string) => {
  const currentOrganizationId = useCurrentOrganizationId();
  const { data: planCapabilities } = usePlanCapabilities(planId);
  const { data: orgaCapabilities } = useOrganizationCapabilities(
    currentOrganizationId || organisationId,
  );
  return planId ? planCapabilities : orgaCapabilities;
};

const typePriority: OfferType[] = [
  OfferType.OPTION,
  OfferType.SUBSCRIPTION,
  OfferType.SUPPORT,
  OfferType.TARGETING,
];
export const useCapabilitiesFirstOffer = (feature?: Feature) => {
  const { data: capabilitiesOffers } = useCapabilitiesOffers();

  return useMemo(() => {
    if (!feature || !capabilitiesOffers || !capabilitiesOffers[feature]) {
      return null;
    }

    const { offers } = capabilitiesOffers[feature];
    if (!offers?.length) {
      return null;
    }

    let selected = offers[0];

    for (const current of offers) {
      if (!current.shortName && selected.shortName) {
        continue;
      }

      if (
        typePriority.indexOf(current.type) < typePriority.indexOf(selected.type)
      ) {
        selected = current;
      } else if (
        (current.referencePrice || 0) < (selected.referencePrice || 0)
      ) {
        selected = current;
      }
    }

    return selected;
  }, [capabilitiesOffers, feature]);
};
